import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import React, { ChangeEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import AddInIds from "@/constants/AddInIds";
import { PAGE_CONFIGS, PageType } from "@/constants/PageType";
import { useAddinData } from "@/hooks/addins/useAddinData";
import { useRemoveAddIn } from "@/hooks/addins/useRemoveAddIn";
import { useUpdateAddIn } from "@/hooks/addins/useUpdateAddIn";
import usePresets from "@/hooks/presets/usePresets";
import { DataGridSettingsAddInData } from "@/models/DataGridSettingsAddInData";

const TEMPLATE_PREFIX = "template-";
const TABLE_HEIGHT = 300;

const PAGE_TYPE_OPTIONS: { value: PageType; labelKey: string }[] = [
  { value: "diagnosticMonitor", labelKey: "admin.templateManager.diagnosticMonitor" },
  { value: "statusMonitor", labelKey: "admin.templateManager.statusMonitor" },
  { value: "faultMonitor", labelKey: "admin.templateManager.faultMonitor" },
  { value: "deviceFirmware", labelKey: "admin.templateManager.deviceFirmware" },
];

function parseTemplateId(id: string): { preset: string; name: string } {
  const withoutPrefix = id.slice(TEMPLATE_PREFIX.length);
  const firstDash = withoutPrefix.indexOf("-");
  if (firstDash === -1) return { preset: withoutPrefix, name: "" };
  return {
    preset: withoutPrefix.slice(0, firstDash),
    name: withoutPrefix.slice(firstDash + 1),
  };
}

/** Extract diagnostic IDs referenced in a template's settings. */
function extractReferencedDiagnosticIds(template: DataGridSettingsAddInData): string[] {
  const ids = new Set<string>();

  // From dataGridState
  try {
    const state = JSON.parse(template.dataGridState);
    const orderedFields: string[] | undefined = state?.columns?.orderedFields;
    if (orderedFields) {
      for (const field of orderedFields) ids.add(field);
    }
    const visibilityModel: Record<string, boolean> | undefined =
      state?.columns?.columnVisibilityModel;
    if (visibilityModel) {
      for (const key of Object.keys(visibilityModel)) ids.add(key);
    }
  } catch {
    // dataGridState may be malformed — skip
  }

  // From keyed settings
  if (template.precisions) {
    for (const key of Object.keys(template.precisions)) ids.add(key);
  }
  if (template.colorIntervals) {
    for (const key of Object.keys(template.colorIntervals)) ids.add(key);
  }
  if (template.columnNames) {
    for (const key of Object.keys(template.columnNames)) ids.add(key);
  }

  // Remove well-known non-diagnostic fields
  const builtInFields = [
    "__check__",
    "__reorder__",
    "__detail_panel_toggle__",
    "device",
    "groups",
    "address",
    "lastContact",
    "parking",
    "remark",
    "remainingRange",
  ];
  for (const f of builtInFields) ids.delete(f);

  return [...ids];
}

const TemplateBulkManager: React.FC = () => {
  const { t } = useTranslation();
  const [pageType, setPageType] = useState<PageType>("diagnosticMonitor");
  const addInId = PAGE_CONFIGS[pageType].addInId;

  const { data: allData = [], isLoading, error } = useAddinData<DataGridSettingsAddInData>(addInId);
  const { data: presets = [] } = usePresets();
  const faultPresetsResult = useAddinData<DataGridSettingsAddInData>(AddInIds.faultPresets);

  const updateAddIn = useUpdateAddIn<DataGridSettingsAddInData>(addInId, "id");
  const removeAddIn = useRemoveAddIn(addInId, "id");

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [importInProgress, setImportInProgress] = useState(false);

  const [missingColumnsDialog, setMissingColumnsDialog] = useState<{
    open: boolean;
    templateName: string;
    missingIds: string[];
    resolve: (proceed: boolean) => void;
  }>({
    open: false,
    templateName: "",
    missingIds: [],
    resolve: () => {
      return;
    },
  });

  const templates = allData.filter(d => d.id.startsWith(TEMPLATE_PREFIX));

  const handleSelectAllClick = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedIds(templates.map(t => t.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleClick = (id: string) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id],
    );
  };

  const handleExport = () => {
    const toExport = templates.filter(t => selectedIds.includes(t.id));
    const json = JSON.stringify(toExport, null, 2);
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `templates-${pageType}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const getPresetsForPageType = () => {
    if (pageType === "faultMonitor") {
      return (faultPresetsResult.data ?? []) as unknown as { id: string; description?: string; columns?: { diagnostic: { id: string } }[] }[];
    }
    return presets;
  };

  const handleImport = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async e => {
      try {
        const imported: DataGridSettingsAddInData[] = JSON.parse(e.target?.result as string);
        setImportInProgress(true);
        const currentPresets = getPresetsForPageType();

        for (const template of imported) {
          // 1. Duplicate check
          if (templates.find(t => t.id === template.id)) {
            alert(t("admin.templateManager.alreadyExists", { name: template.id }));
            continue;
          }

          // 2. Preset existence check
          const { preset: presetName, name: templateName } = parseTemplateId(template.id);
          const matchingPreset = currentPresets.find(p => p.description === presetName);

          if (!matchingPreset) {
            alert(t("admin.templateManager.presetNotFound", { preset: presetName, template: templateName }));
            continue;
          }

          // 3. Column validation
          const referencedIds = extractReferencedDiagnosticIds(template);
          const presetDiagnosticIds = new Set(
            (matchingPreset.columns ?? []).map(col => col.diagnostic.id),
          );
          const missingIds = referencedIds.filter(id => !presetDiagnosticIds.has(id));

          if (missingIds.length > 0) {
            const proceed = await new Promise<boolean>(resolve => {
              setMissingColumnsDialog({
                open: true,
                templateName: `${presetName} > ${templateName}`,
                missingIds,
                resolve,
              });
            });

            if (!proceed) continue;
          }

          // 4. Save
          await updateAddIn.mutateAsync(template);
        }
      } catch (error) {
        console.error("Failed to import templates:", error);
        alert(t("admin.templateManager.importFailed"));
      } finally {
        setImportInProgress(false);
        event.target.value = "";
      }
    };
    reader.readAsText(file);
  };

  const handleDelete = async () => {
    for (const id of selectedIds) {
      await removeAddIn.mutateAsync({ delSearch: id });
    }
    setSelectedIds([]);
  };

  const handleMissingColumnsConfirm = () => {
    missingColumnsDialog.resolve(true);
    setMissingColumnsDialog({ open: false, templateName: "", missingIds: [], resolve: () => { return; } });
  };

  const handleMissingColumnsCancel = () => {
    missingColumnsDialog.resolve(false);
    setMissingColumnsDialog({ open: false, templateName: "", missingIds: [], resolve: () => { return; } });
  };

  return (
    <Box>
      <Box sx={{ display: "flex", gap: 2, mb: 2, alignItems: "center" }}>
        <FormControl size="small" sx={{ minWidth: 250 }}>
          <InputLabel>{t("admin.templateManager.pageType")}</InputLabel>
          <Select
            value={pageType}
            label={t("admin.templateManager.pageType")}
            onChange={e => {
              setPageType(e.target.value as PageType);
              setSelectedIds([]);
            }}
          >
            {PAGE_TYPE_OPTIONS.map(opt => (
              <MenuItem key={opt.value} value={opt.value}>
                {t(opt.labelKey)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button variant="contained" size="small" onClick={handleExport} disabled={selectedIds.length === 0}>
          {t("admin.templateManager.export")}
        </Button>
        <Button variant="contained" size="small" component="label" loading={importInProgress}>
          {t("admin.templateManager.import")}
          <input type="file" accept="application/json" hidden onChange={handleImport} />
        </Button>
        <Button variant="contained" size="small" onClick={handleDelete} disabled={selectedIds.length === 0}>
          {t("admin.templateManager.delete")}
        </Button>
      </Box>

      <TableContainer component={Paper} variant="outlined" sx={{ height: TABLE_HEIGHT }}>
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
            <Typography color="error">{t("admin.templateManager.loadError")}</Typography>
          </Box>
        ) : (
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={
                      selectedIds.length > 0 && selectedIds.length < templates.length
                    }
                    checked={templates.length > 0 && selectedIds.length === templates.length}
                    onChange={handleSelectAllClick}
                  />
                </TableCell>
                <TableCell>{t("admin.templateManager.columnPreset")}</TableCell>
                <TableCell>{t("admin.templateManager.columnName")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {templates.map(template => {
                const { preset, name } = parseTemplateId(template.id);
                const isSelected = selectedIds.includes(template.id);
                return (
                  <TableRow
                    key={template.id}
                    hover
                    onClick={() => handleClick(template.id)}
                    role="checkbox"
                    aria-checked={isSelected}
                    selected={isSelected}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox checked={isSelected} />
                    </TableCell>
                    <TableCell>{preset}</TableCell>
                    <TableCell>{name}</TableCell>
                  </TableRow>
                );
              })}
              {templates.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    <Typography variant="body2" color="text.secondary">
                      {t("admin.templateManager.noTemplates")}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {/* Missing Columns Warning Dialog */}
      <Dialog open={missingColumnsDialog.open}>
        <DialogTitle>{t("admin.templateManager.missingColumnsTitle")}</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            {t("admin.templateManager.missingColumnsMessage", {
              template: missingColumnsDialog.templateName,
              count: missingColumnsDialog.missingIds.length,
            })}
          </Typography>
          <Box component="ul" sx={{ maxHeight: 200, overflow: "auto" }}>
            {missingColumnsDialog.missingIds.map(id => (
              <li key={id}>
                <Typography variant="body2" fontFamily="monospace">
                  {id}
                </Typography>
              </li>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleMissingColumnsCancel}>{t("admin.templateManager.skip")}</Button>
          <Button variant="contained" onClick={handleMissingColumnsConfirm}>
            {t("admin.templateManager.importAnyway")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TemplateBulkManager;
